import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Button, Card, Input, Radio, Select, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { FormEvent } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import {
    DateRangePicker as ZhDateRangePicker,
    Dialog as ZhDialog,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import request from "@/api";
import { prodTypeService } from "@/api/services/prodTypeService";
import { dateTimeFormat, getDaiBanState } from "@/utils";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import { notifyOperationSuccess } from "@/utils/operationFeedback";
import { useUserStore } from "@/views/stores";
import type { SampleApply, SampleApplyHandleForm } from "@zhihaoscm/entity";

export interface SampleApplyListResult {
    list: SampleApply[];
    total: number;
}

export interface SampleApplyQuery {
    page: number;
    size: number;
    productId?: string;
    keyword?: string;
    productTypeId?: string;
    state?: number;
    beginTime?: string;
    endTime?: string;
    sortKey?: string;
    sortOrder?: string;
}

export interface SampleApplyPageProps {
    loadApplications?: (query: SampleApplyQuery) => Promise<SampleApplyListResult>;
    handleApplication?: (id: number | string, payload: SampleApplyHandleForm) => Promise<unknown>;
    permissions?: string[];
}

const STATE_LABELS: Record<number, string> = {
    1: "待处理",
    2: "已寄出",
    3: "无效信息"
};

const STATE_OPTIONS = Object.entries(STATE_LABELS).map(([value, label]) => ({ value, label }));
const DEFAULT_SAMPLE_APPLY_QUERY: SampleApplyQuery = { page: 1, size: 10 };
const PRODUCT_DETAIL_PERMISSIONS = ["ROLE_PRODUCT_R", "ROLE_PRODUCT_MANAGE", "ROLE_PRODUCT_DEAL", "ROLE_PRODUCT_CLOSE"];
const CUSTOMER_DETAIL_PERMISSION = "ROLE_SS_CUSTOMER_R";

/** 读取旧版待办跳转缓存，保持从待办进入申请寄样列表时的状态筛选。 */
function getInitialSampleApplyQuery(): SampleApplyQuery {
    const states = getDaiBanState("state");
    const state = Number(states[0]);
    return states.length && Number.isFinite(state)
        ? { ...DEFAULT_SAMPLE_APPLY_QUERY, state }
        : DEFAULT_SAMPLE_APPLY_QUERY;
}

/** 沿用 Vue 申请寄样分页接口。 */
export async function loadSampleApplications(query: SampleApplyQuery): Promise<SampleApplyListResult> {
    const response = await request.service.get<SampleApplyListResult>("/admin/sample-apply/paging", { params: query });
    return response.data;
}

/** 沿用 Vue 申请寄样列表的处理接口。 */
export async function handleSampleApplication(id: number | string, payload: SampleApplyHandleForm): Promise<unknown> {
    const response = await request.service.put(`/admin/sample-apply/handle/${id}`, payload);
    return response.data;
}

/** 将 Vue 申请用户字段映射为列表展示文案。 */
function getCustomerLabel(row: SampleApply): string {
    const customer = row.customerEnterprise;
    return `${customer?.name || customer?.realName || ""} | ${customer?.mobile || ""}`;
}

/** 将 Vue 收货地址字段映射为列表展示文案。 */
function getAddressLabel(row: SampleApply): string {
    const address = row.addressInfo;
    return `${address?.contacts || ""} ${address?.cellphone || ""} | ${address?.detailAddress || ""}`;
}

/** 申请寄样列表首屏，保留 Vue 核心列、默认分页和待处理权限入口。 */
export function SampleApplyPage({
    loadApplications = loadSampleApplications,
    handleApplication = handleSampleApplication,
    permissions
}: SampleApplyPageProps) {
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    // 沿用 Vue 全局封禁态，申请寄样处理入口保留但禁止触发。
    const banButton = useUserStore(state => state.banButton);
    const currentPermissions = permissions ?? storePermissions;
    // 跨模块详情链接沿用目标路由权限，避免寄样权限用户点击后被路由守卫重定向到 403。
    const canViewProductDetail = currentPermissions.some(permission => PRODUCT_DETAIL_PERMISSIONS.includes(permission));
    const canViewCustomerDetail = currentPermissions.includes(CUSTOMER_DETAIL_PERMISSION);
    const initialQuery = getInitialSampleApplyQuery();
    const [draftFilters, setDraftFilters] = useState({
        productId: "",
        keyword: "",
        productTypeId: "",
        state: initialQuery.state == null ? "" : String(initialQuery.state),
        beginDate: "",
        endDate: ""
    });
    const [query, setQuery] = useState<SampleApplyQuery>(initialQuery);
    const [items, setItems] = useState<SampleApply[]>([]);
    const [total, setTotal] = useState(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    // 处理表单校验独立于列表错误，避免提示跑到表格筛选区域。
    const [dialogError, setDialogError] = useState<{
        field: "state" | "logisticsNumber" | null;
        message: string;
    } | null>(null);
    const [currentRow, setCurrentRow] = useState<SampleApply | null>(null);
    const [handleState, setHandleState] = useState<number | undefined>();
    const [logisticsNumber, setLogisticsNumber] = useState("");
    const [handleRemark, setHandleRemark] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [productTypeOptions, setProductTypeOptions] = useState<Array<{ label: string; value: string }>>([]);
    const [productTypeLoading, setProductTypeLoading] = useState(false);

    const hasPermission = useCallback(
        (permission: string) => currentPermissions.includes(permission),
        [currentPermissions]
    );

    /** 打开 Vue 处理弹窗并清空上一次表单内容。 */
    const openHandleDialog = (row: SampleApply) => {
        setCurrentRow(row);
        setHandleState(undefined);
        setLogisticsNumber("");
        setHandleRemark("");
        setDialogError(null);
        setErrorMessage(null);
    };

    /** 校验并提交 Vue 申请寄样处理 payload，成功后刷新当前分页。 */
    const submitHandle = async () => {
        if (!currentRow || handleState == null) {
            setDialogError({ field: "state", message: "请选择处理结果" });
            return;
        }
        if (handleState === 2 && !logisticsNumber.trim()) {
            setDialogError({ field: "logisticsNumber", message: "请填写寄出物流单号" });
            return;
        }
        setSubmitting(true);
        try {
            await handleApplication(currentRow.id, {
                state: handleState,
                logisticsNumber: handleState === 3 ? "" : logisticsNumber.trim(),
                handleRemark: handleRemark.trim()
            });
            notifyOperationSuccess("处理成功");
            setCurrentRow(null);
            setDialogError(null);
            setErrorMessage(null);
            try {
                const result = await loadApplications(query);
                setItems(result.list || []);
                setTotal(Number(result.total) || 0);
            } catch {
                setErrorMessage("申请寄样列表加载失败");
            }
        } catch {
            setDialogError({ field: null, message: "申请寄样处理失败" });
        } finally {
            setSubmitting(false);
        }
    };

    const updateFilter = (key: keyof typeof draftFilters, value: string) => {
        setDraftFilters(current => ({ ...current, [key]: value }));
    };

    /** 沿用旧 Vue SelectRemote 的品类搜索契约，候选请求只传关键词。 */
    const searchProductTypes = async (keyword = "") => {
        const normalizedKeyword = keyword.trim();
        if (!normalizedKeyword) {
            setProductTypeOptions([]);
            return;
        }
        setProductTypeLoading(true);
        try {
            const result = await prodTypeService.selector(
                undefined,
                undefined,
                undefined,
                normalizedKeyword
            );
            setProductTypeOptions(
                result.map(item => ({
                    label: item.areaAbbreviation ? `${item.name} | ${item.areaAbbreviation}` : item.name,
                    value: String(item.id)
                }))
            );
        } catch {
            setProductTypeOptions([]);
        } finally {
            setProductTypeLoading(false);
        }
    };

    /** 重置 Vue 申请寄样筛选条件，并回到默认第一页。 */
    const resetFilters = () => {
        setDraftFilters({
            productId: "",
            keyword: "",
            productTypeId: "",
            state: "",
            beginDate: "",
            endDate: ""
        });
        setErrorMessage(null);
        setQuery(current => ({ page: 1, size: current.size }));
    };

    /** 将筛选控件转换为 Vue 查询参数，日期统一补齐当天起止时间。 */
    const submitFilters = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const beginDate = draftFilters.beginDate.trim();
        const endDate = draftFilters.endDate.trim();
        if (Boolean(beginDate) !== Boolean(endDate)) {
            setErrorMessage("请选择完整申请日期范围");
            return;
        }
        if (beginDate && endDate && beginDate > endDate) {
            setErrorMessage("申请开始日期不能晚于结束日期");
            return;
        }
        const nextQuery: SampleApplyQuery = { page: 1, size: query.size };
        if (draftFilters.productId.trim()) nextQuery.productId = draftFilters.productId.trim();
        if (draftFilters.keyword.trim()) nextQuery.keyword = draftFilters.keyword.trim();
        if (draftFilters.productTypeId.trim()) nextQuery.productTypeId = draftFilters.productTypeId.trim();
        if (draftFilters.state) nextQuery.state = Number(draftFilters.state);
        if (beginDate && endDate) {
            nextQuery.beginTime = `${beginDate}T00:00:00`;
            nextQuery.endTime = `${endDate}T23:59:59`;
        }
        setErrorMessage(null);
        setQuery(nextQuery);
    };

    useEffect(() => {
        let disposed = false;
        void loadApplications(query)
            .then(result => {
                if (!disposed) {
                    setItems(result.list || []);
                    setTotal(Number(result.total) || 0);
                    // 旧 Vue useTable 只消费一次待办筛选，避免后续普通进入继续复用旧状态。
                    localStorage.removeItem("MytaskGotoListPageQuery");
                }
            })
            .catch(() => {
                if (!disposed) {
                    setItems([]);
                    setTotal(0);
                    setErrorMessage("申请寄样列表加载失败");
                }
            });
        return () => {
            disposed = true;
        };
    }, [loadApplications, query]);

    // 保留旧 Vue ProTable 的申请时间排序参数，zhihao-ui 的方向映射为旧接口 sortKey/sortOrder。
    const handleSortChange = useCallback(
        ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
            if (prop !== "createdTime") return;
            setQuery(current => {
                const nextQuery = { ...current, page: 1 };
                if (!order) {
                    delete nextQuery.sortKey;
                    delete nextQuery.sortOrder;
                } else {
                    nextQuery.sortKey = "created_time";
                    nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
                }
                return nextQuery;
            });
        },
        []
    );

    // 用 zhihao-ui-react 列契约承接旧 Vue 申请寄样 ProTable 的展示字段和操作插槽。
    const columns = useMemo<TableColumnProps<SampleApply>[]>(
        () => [
            {
                key: "id",
                label: "序号",
                width: 92,
                prop: "id",
                render: ({ row }) => <Link to={`/userOperation/sampleApply/detail?id=${row.id}`}>{row.id}</Link>
            },
            {
                key: "productId",
                label: "商品编号",
                width: 176,
                prop: "productId",
                render: ({ row }) =>
                    canViewProductDetail ? (
                        <Link to={`/productManagement/consignment/detail?id=${row.productId}`}>{row.productId}</Link>
                    ) : (
                        row.productId || "-"
                    )
            },
            {
                key: "customer",
                label: "申请用户",
                minWidth: 365,
                prop: "customerEnterprise",
                render: ({ row }) =>
                    canViewCustomerDetail ? (
                        <Link to={`/lianYunUser/userInfo?userId=${row.customerId}`}>{getCustomerLabel(row)}</Link>
                    ) : (
                        getCustomerLabel(row)
                    )
            },
            {
                key: "title",
                label: "商品标题",
                minWidth: 180,
                prop: "productInfo.title",
                render: ({ row }) => row.productInfo?.title || "-"
            },
            {
                key: "productType",
                label: "品类",
                width: 176,
                prop: "productTypeId",
                render: ({ row }) =>
                    `${row.productInfo?.productTypeName || ""} (${row.productInfo?.productTypeSpec || ""})`
            },
            {
                key: "address",
                label: "收货信息",
                width: 420,
                prop: "addressInfo",
                render: ({ row }) => getAddressLabel(row)
            },
            {
                key: "createdTime",
                label: "申请时间",
                width: 154,
                prop: "createdTime",
                sortable: true,
                render: ({ row }) => dateTimeFormat(row.createdTime)
            },
            {
                key: "state",
                label: "状态",
                width: 92,
                prop: "state",
                render: ({ row }) => STATE_LABELS[Number(row.state)] || "-"
            },
            {
                key: "operation",
                label: "操作",
                prop: "operation",
                fixed: "right",
                width: 80,
                className: "admin-table-operation-column compact",
                labelClassName: "admin-table-operation-column compact",
                render: ({ row }) =>
                    Number(row.state) === 1 && hasPermission("ROLE_APPLY_SAMPLE_DEAL") ? (
                        <Button aria-label="处理" disabled={banButton} onClick={() => openHandleDialog(row)}>
                            处理
                        </Button>
                    ) : null
            }
        ],
        [banButton, canViewCustomerDetail, canViewProductDetail, hasPermission]
    );

    return (
        <section aria-label="sample-apply-page" className="flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col">
                <ZhTable<SampleApply>
                    columns={normalizeOperationColumns(columns, items)}
                    data={items}
                    pagination={false}
                    rowKey="id"
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                用户申请寄样
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <form className="admin-table-filter-form mb-4" onSubmit={submitFilters}>
                                <label>
                                    商品编号
                                    <Input
                                        className="sample-apply-product-filter"
                                        aria-label="商品编号"
                                        placeholder="请输入商品编号搜索"
                                        value={draftFilters.productId}
                                        onChange={event => updateFilter("productId", event.target.value)}
                                    />
                                </label>
                                <label>
                                    申请用户
                                    <Input
                                        className="sample-apply-customer-filter"
                                        aria-label="申请用户"
                                        placeholder="请输入申请人账号信息搜索"
                                        value={draftFilters.keyword}
                                        onChange={event => updateFilter("keyword", event.target.value)}
                                    />
                                </label>
                                <label>
                                    品类编号
                                    <Select
                                        aria-label="品类编号"
                                        className="sample-apply-product-type-filter"
                                        allowClear
                                        filterOption={false}
                                        loading={productTypeLoading}
                                        options={productTypeOptions}
                                        placeholder="请选择品类"
                                        showSearch
                                        value={draftFilters.productTypeId || undefined}
                                        onChange={value => updateFilter("productTypeId", value || "")}
                                        onClear={() => updateFilter("productTypeId", "")}
                                        onSearch={value => void searchProductTypes(value)}
                                    />
                                </label>
                                <ZhDateRangePicker
                                    type="daterange"
                                    format="YYYY-MM-DD"
                                    valueFormat="YYYY-MM-DD"
                                    rangeSeparator="-"
                                    startPlaceholder="申请开始日期"
                                    endPlaceholder="申请结束日期"
                                    value={[draftFilters.beginDate, draftFilters.endDate]}
                                    onChange={value => {
                                        updateFilter("beginDate", String(value?.[0] || ""));
                                        updateFilter("endDate", String(value?.[1] || ""));
                                    }}
                                />
                                <label>
                                    状态
                                    <Select
                                        aria-label="状态"
                                        options={STATE_OPTIONS}
                                        placeholder="请选择状态"
                                        value={draftFilters.state || undefined}
                                        onChange={value => updateFilter("state", value)}
                                    />
                                </label>
                                <Button
                                    aria-label="查询申请寄样"
                                    htmlType="submit"
                                    icon={<TableSearchIcon name="search" />}
                                    type="primary"
                                >
                                    搜索
                                </Button>
                                <Button
                                    aria-label="重置申请寄样"
                                    className="table-filter-reset-button"
                                    icon={<TableSearchIcon name="reset" />}
                                    onClick={resetFilters}
                                />
                            </form>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                hideOnSinglePage
                                onChange={(page, pageSize) =>
                                    setQuery(current => ({
                                        ...current,
                                        // 切换每页条数后回到第一页，普通翻页保留当前页。
                                        page: pageSize !== current.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
            <ZhDialog
                alignCenter
                modalClass="user-operation-sample-dialog"
                open={currentRow !== null}
                title="处理"
                width={400}
                onCancel={() => {
                    setCurrentRow(null);
                    setDialogError(null);
                }}
                footer={
                    <div className="flex justify-end gap-2">
                        <Button
                            onClick={() => {
                                setCurrentRow(null);
                                setDialogError(null);
                            }}
                        >
                            取消
                        </Button>
                        <Button
                            aria-label="提交处理"
                            loading={submitting}
                            onClick={() => void submitHandle()}
                            type="primary"
                        >
                            提交
                        </Button>
                    </div>
                }
            >
                {dialogError?.field === null ? (
                    <Typography.Text className="user-operation-action-dialog-error" role="alert" type="danger">
                        {dialogError.message}
                    </Typography.Text>
                ) : null}
                <div className="user-operation-action-dialog-form">
                    <div className="user-operation-action-dialog-form-item user-operation-action-dialog-form-item--sample">
                        <span className="user-operation-action-dialog-form-label">
                            <span className="user-operation-action-dialog-required">*</span>
                            处理结果
                        </span>
                        <span className="user-operation-action-dialog-form-control">
                            <Radio.Group
                                aria-label="处理结果"
                                className="user-operation-action-dialog-radio-group"
                                value={handleState}
                                onChange={event => {
                                    setHandleState(event.target.value);
                                    setDialogError(null);
                                }}
                            >
                                <Radio value={2}>已寄出</Radio>
                                <Radio value={3}>无效信息</Radio>
                            </Radio.Group>
                            {dialogError?.field === "state" ? (
                                <Typography.Text
                                    className="user-operation-action-dialog-validation-error"
                                    role="alert"
                                    type="danger"
                                >
                                    {dialogError.message}
                                </Typography.Text>
                            ) : null}
                        </span>
                    </div>
                    {handleState === 2 ? (
                        <label className="user-operation-action-dialog-form-item user-operation-action-dialog-form-item--sample">
                            <span className="user-operation-action-dialog-form-label">物流单号</span>
                            <span className="user-operation-action-dialog-form-control">
                                <Input
                                    aria-label="物流单号"
                                    maxLength={100}
                                    placeholder="请填写寄出物流单号"
                                    value={logisticsNumber}
                                    onChange={event => {
                                        setLogisticsNumber(event.target.value);
                                        setDialogError(current =>
                                            current?.field === "logisticsNumber" ? null : current
                                        );
                                    }}
                                />
                                {dialogError?.field === "logisticsNumber" ? (
                                    <Typography.Text
                                        className="user-operation-action-dialog-validation-error"
                                        role="alert"
                                        type="danger"
                                    >
                                        {dialogError.message}
                                    </Typography.Text>
                                ) : null}
                            </span>
                        </label>
                    ) : null}
                    <label className="user-operation-action-dialog-form-item user-operation-action-dialog-form-item--sample">
                        <span className="user-operation-action-dialog-form-label">备注</span>
                        <span className="user-operation-action-dialog-form-control">
                            <Input.TextArea
                                aria-label="处理备注"
                                className="user-operation-action-dialog-textarea"
                                maxLength={100}
                                placeholder="请输入备注"
                                rows={3}
                                showCount
                                value={handleRemark}
                                onChange={event => setHandleRemark(event.target.value)}
                            />
                        </span>
                    </label>
                </div>
            </ZhDialog>
        </section>
    );
}

export default SampleApplyPage;
